import React from 'react';
import { Target, CheckCircle2, CalendarClock, Receipt, TrendingUp, DollarSign, AlertCircle, Wallet } from 'lucide-react';

const CLP = (n) => (Number(n) || 0).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

/**
 * Fila de 8 tarjetas de resumen para las tablas de Gerencia.
 * Se alimenta de una lista de filas con los campos:
 *   realizadas_count, agendadas_count (fallback: total_surgeries),
 *   facturado_realizado, venta_bruta, recaudacion_real, brecha_cobro.
 *
 * Props:
 *   - rows: array de filas (consultora o doctor)
 *   - idPrefix: prefijo para `data-testid` (p.ej. 'consultora' | 'doctor')
 */
export function ProjectionSummaryCards({ rows, idPrefix = 'summary' }) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return null;

  const realizadas = list.reduce((a, c) => a + (c.realizadas_count || 0), 0);
  // `by_consultora_projection` usa `total_surgeries`; `by_doctor` usa `agendadas_count`.
  const agendadas = list.reduce(
    (a, c) => a + (c.agendadas_count != null ? c.agendadas_count : (c.total_surgeries || 0)),
    0,
  );
  const totalCirugias = realizadas + agendadas;
  const facturadoRealizado = list.reduce((a, c) => a + (c.facturado_realizado || 0), 0);
  const ventaBruta = list.reduce((a, c) => a + (c.venta_bruta || 0), 0);
  const recaudacionReal = list.reduce((a, c) => a + (c.recaudacion_real || 0), 0);
  const brechaCobro = list.reduce((a, c) => a + (c.brecha_cobro || 0), 0);
  const montoTotal = facturadoRealizado + ventaBruta;

  const cards = [
    { label: 'Total Cirugías',      value: totalCirugias,      Icon: Target,        accent: 'text-[#1A1C18] bg-[#F3EBE1]',    tone: 'text-[#1A1C18]',   testid: `${idPrefix}-summary-total` },
    { label: 'Cant. Realizadas',    value: realizadas,         Icon: CheckCircle2,  accent: 'text-emerald-700 bg-emerald-50', tone: 'text-emerald-700', testid: `${idPrefix}-summary-realizadas` },
    { label: 'Agendadas',           value: agendadas,          Icon: CalendarClock, accent: 'text-[#8B5CF6] bg-[#8B5CF6]/10', tone: 'text-[#8B5CF6]',   testid: `${idPrefix}-summary-agendadas` },
    { label: 'Facturado Realizado', value: facturadoRealizado, Icon: Receipt,       accent: 'text-emerald-800 bg-emerald-50', tone: 'text-emerald-800', isMoney: true, testid: `${idPrefix}-summary-facturado-realizado` },
    { label: 'Venta Bruta',         value: ventaBruta,         Icon: TrendingUp,    accent: 'text-[#1A1C18] bg-[#F3EBE1]',    tone: 'text-[#1A1C18]',   isMoney: true, testid: `${idPrefix}-summary-venta-bruta` },
    { label: 'Recaudación Real',    value: recaudacionReal,    Icon: DollarSign,    accent: 'text-emerald-700 bg-emerald-50', tone: 'text-emerald-700', isMoney: true, testid: `${idPrefix}-summary-recaudacion` },
    { label: 'Brecha de Cobro',     value: brechaCobro,        Icon: AlertCircle,   accent: 'text-red-700 bg-red-50',         tone: 'text-red-700',     isMoney: true, testid: `${idPrefix}-summary-brecha` },
    { label: 'Monto Total',         value: montoTotal,         Icon: Wallet,        accent: 'text-[#2B4C3B] bg-[#2B4C3B]/10', tone: 'text-[#2B4C3B]',   isMoney: true, testid: `${idPrefix}-summary-monto-total` },
  ];

  return (
    <div
      className="px-6 py-4 border-b border-[#E8E5DD] grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3"
      data-testid={`${idPrefix}-summary-cards`}
    >
      {cards.map((c) => (
        <div key={c.label} className="rounded-lg border border-[#E8E5DD] bg-white p-3 flex flex-col gap-2" data-testid={c.testid}>
          <div className="flex items-center gap-2">
            <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${c.accent}`}>
              <c.Icon size={14} />
            </span>
            <span className="text-[11px] font-medium text-[#6A736B] leading-tight">{c.label}</span>
          </div>
          <span className={`text-lg font-bold font-mono tabular-nums ${c.tone}`}>
            {c.isMoney ? CLP(c.value) : (c.value || 0).toLocaleString('es-CL')}
          </span>
        </div>
      ))}
    </div>
  );
}
